import type { TypesenseSearchParams } from '~/utils/typesenseClient'
import {
  TYPESENSE_GEO_FIELD,
  buildTypesenseGeoSort,
} from '~/utils/typesenseGeo'

export interface TypesenseSimilarEventsSourceDoc {
  id: string
  city_slug: string
  /** Typesense `geopoint[]` — one `[lat, lng]` per event location. */
  _geoloc?: [number, number][]
}

/** Only the fields the similar-events queries build from — never the 384-float embedding. */
export const SIMILAR_EVENTS_SOURCE_FIELDS = `id,city_slug,${TYPESENSE_GEO_FIELD}`

const SIMILAR_EVENTS_LIMIT = 8

/**
 * Nearby-first tuning (KFT-4145) — expect to iterate. The `k` nearest-by-meaning
 * events form the pool that proximity then reorders: a larger `k` surfaces more
 * local but less similar events. Within each precision bucket (0–50 km, 50–100
 * km, …) events are ordered by similarity.
 */
export const SIMILAR_EVENTS_VECTOR_K = 100
export const SIMILAR_EVENTS_GEO_PRECISION_KM = 50

const sourceCoords = (source: TypesenseSimilarEventsSourceDoc) => {
  const [lat, lng] = source._geoloc?.[0] ?? []
  return Number.isFinite(lat) && Number.isFinite(lng)
    ? { lat: lat!, lng: lng! }
    : undefined
}

/**
 * Nearest neighbors of the source event's own stored vector, nearby events
 * first — no query-time embedding needed. Online events are excluded: they have
 * no city and a placeholder `_geoloc`. Without source coords, similarity only.
 */
export const buildVectorSimilarEventsQuery = (
  source: TypesenseSimilarEventsSourceDoc,
  collection: string
): TypesenseSearchParams => {
  const sortBy = buildTypesenseGeoSort(
    sourceCoords(source),
    '_vector_distance:asc',
    SIMILAR_EVENTS_GEO_PRECISION_KM
  )
  const k = sortBy ? SIMILAR_EVENTS_VECTOR_K : SIMILAR_EVENTS_LIMIT
  return {
    collection,
    q: '*',
    query_by: 'title',
    filter_by: `id:!=${source.id} && has_events:true && is_online:false`,
    vector_query: `embedding:([], id: ${source.id}, k: ${k})`,
    per_page: SIMILAR_EVENTS_LIMIT,
    ...(sortBy ? { sort_by: sortBy } : {}),
  }
}

/** Vector variant of the above: nearest neighbors of the source event's vector, restricted to online events. */
export const buildVectorSimilarOnlineEventsQuery = (
  source: TypesenseSimilarEventsSourceDoc,
  collection: string
): TypesenseSearchParams => ({
  collection,
  q: '*',
  query_by: 'title',
  filter_by: `id:!=${source.id} && has_events:true && is_online:true`,
  vector_query: `embedding:([], id: ${source.id}, k: ${SIMILAR_EVENTS_LIMIT})`,
  per_page: SIMILAR_EVENTS_LIMIT,
})
